import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { SMLSettingsTabId } from '../../../settings/tabs/types';
import { useEditorContext } from '../SMLEditorContext';
import { EditorStatus } from '../topBar/editorStatus';
import { EditorPublish } from '../topBar/useEditorPublish';
import { pageMessages as m } from './pageMessages';
import { SMLPageAddressCard } from './SMLPageAddressCard';
import { SMLPageNameCard } from './SMLPageNameCard';
import { SMLPageStatusCard } from './SMLPageStatusCard';
import { SMLBioLinkSettingsCard } from './SMLBioLinkSettingsCard';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for several parts; each part uses its own slice of it */
import styles from './SMLPageTab.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SMLPageTabProps {
  status: EditorStatus;
  publish: EditorPublish;
  blockCount: number;
}

/**
 * The Page tab (design a6, e4): the address, the name, the share picture and the status. The Bio
 * Link has no slug: it shows the account address, and the "New songs" card.
 */
const SMLPageTabComponent: React.FC<SMLPageTabProps> = ({ status, publish, blockCount }) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const { isBioLink, slugPrefix } = useEditorContext();
  const openArtistSettings = useCallback(
    () => router.push(SMLRoutes.settings, { tab: SMLSettingsTabId.artist }),
    [router]
  );
  const openStudio = useCallback(() => router.push(SMLRoutes.promoteStudio), [router]);

  return (
    <div className={styles.tab} data-testid="editor-page-tab">
      {isBioLink && <SMLBioLinkSettingsCard />}
      {isBioLink ? (
        <section className={styles.card}>
          <h4 className={styles.cardTitle}>{intl.formatMessage(m.address)}</h4>
          <span className={styles.addressText}>{slugPrefix.replace(/\/$/, '')}</span>
          <p className={styles.help}>{intl.formatMessage(m.bioAddressHelp)}</p>
          <button type="button" className={styles.link} onClick={openArtistSettings}>
            {intl.formatMessage(m.openArtistSettings)}
          </button>
        </section>
      ) : (
        <SMLPageAddressCard />
      )}
      <SMLPageNameCard />
      {!isBioLink && (
        <section className={styles.card}>
          <h4 className={styles.cardTitle}>{intl.formatMessage(m.share)}</h4>
          <p className={styles.help}>{intl.formatMessage(m.shareHelp)}</p>
          <button type="button" className={styles.link} onClick={openStudio}>
            {intl.formatMessage(m.openStudio)}
          </button>
        </section>
      )}
      <SMLPageStatusCard status={status} publish={publish} blockCount={blockCount} />
    </div>
  );
};

export const SMLPageTab = TypedMemo(SMLPageTabComponent);
